import type {
  VixiCaptureFields,
  VixiMagicFields,
  VixiStreamingFields,
  VixiLightshowFields,
  VixiAddonsFields,
  VixiLicensingFields,
  BlogPost,
  FAQ,
  Testimonial,
  TeamMember,
  Project,
  FeaturedEvent,
  HeroSettings,
  ContactSettings,
  NavigationSettings,
  FooterSettings,
  PartnerLogo,
  PartnersSectionSettings,
  HomepageFields,
  CompanyPageFields,
  SolutionsPageFields,
  VixiMainPageFields,
  WorkPageFields,
  WPPageContent,
  MegaNavSettings,
  MegaNavSection,
  MegaNavItem,
  YoastSEO,
  PatentDisclaimerSettings,
} from '@/types/wordpress';
import type {
  GraphQLResponse,
  GQLFAQsResponse,
  GQLPostsResponse,
  GQLPostNode,
  GQLProjectsResponse,
  GQLProjectNode,
  GQLTestimonialsResponse,
  GQLTeamMembersResponse,
  GQLEventsResponse,
  GQLHeroSettingsResponse,
  GQLContactSettingsResponse,
  GQLNavigationSettingsResponse,
  GQLFooterSettingsResponse,
  GQLPartnerLogosResponse,
  GQLHomepageFieldsResponse,
  GQLCompanyPageFieldsResponse,
  GQLSolutionsPageFieldsResponse,
  GQLWorkPageFieldsResponse,
  GQLMegaNavSettingsResponse,
  GQLYoastPageResponse,
  GQLYoastPostResponse,
  GQLPatentDisclaimerResponse,
  GQLVixiMainPageFieldsResponse,
  GQLVixiCaptureFieldsResponse,
  GQLVixiMagicFieldsResponse,
  GQLVixiStreamingFieldsResponse,
  GQLVixiLightshowFieldsResponse,
  GQLVixiAddonsFieldsResponse,
  GQLVixiLicensingFieldsResponse,
} from '@/types/graphql';

// WordPress GraphQL Client - Rebuilt: 2026-01-29
// Hardcoded WordPress URL (Cloudways hosted)
const WORDPRESS_URL = 'https://wordpress-1585443-6192607.cloudwaysapps.com';
const GRAPHQL_ENDPOINT = `${WORDPRESS_URL}/graphql`;
const GRAPHQL_TIMEOUT_MS = 12_000;

// Supabase Edge Function proxy endpoint (bypasses Cloudways firewall)
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
const PROXY_ENDPOINT = SUPABASE_URL ? `${SUPABASE_URL}/functions/v1/wp-graphql-proxy` : null;

// WordPress endpoints configured

// Check if WordPress is configured
export const isWordPressConfigured = (): boolean => {
  return Boolean(WORDPRESS_URL && WORDPRESS_URL.length > 0);
};

// ============================================
// GRAPHQL CLIENT
// ============================================

async function graphqlRequest<T>(
  query: string,
  variables?: Record<string, unknown>
): Promise<T> {
  if (!isWordPressConfigured()) {
    throw new Error('WordPress URL not configured');
  }

  // Use the Supabase edge function proxy if available (bypasses Cloudways firewall)
  // Fall back to direct WordPress endpoint (works on published site)
  const useProxy = Boolean(PROXY_ENDPOINT);
  const endpoint = useProxy && PROXY_ENDPOINT ? PROXY_ENDPOINT : GRAPHQL_ENDPOINT;
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  
  // Add Supabase anon key when using the proxy
  if (useProxy && SUPABASE_ANON_KEY) {
    headers['apikey'] = SUPABASE_ANON_KEY;
  }

  

  const response = await fetch(endpoint, {
    method: 'POST',
    headers,
    body: JSON.stringify({ query, variables }),
    signal: AbortSignal.timeout(GRAPHQL_TIMEOUT_MS),
  });

  if (!response.ok) {
    throw new Error(`GraphQL request failed: ${response.status} ${response.statusText}`);
  }

  const json: GraphQLResponse<T> = await response.json();

  if (json.errors?.length) {
    throw new Error(`GraphQL error: ${json.errors[0].message}`);
  }

  return json.data;
}

// ============================================
// GRAPHQL QUERIES
// ============================================

const FAQS_QUERY = `
  query GetFAQs {
    faqs(first: 100) {
      nodes {
        databaseId
        title
        content
        faqFields {
          displayOrder
          answer
          field1Question
        }
      }
    }
  }
`;

const POSTS_QUERY = `
  query GetPosts($first: Int = 10) {
    posts(first: $first) {
      nodes {
        databaseId
        slug
        title
        excerpt
        content
        date
        featuredImage {
          node {
            sourceUrl
            altText
          }
        }
        categories {
          nodes {
            name
          }
        }
        author {
          node {
            name
          }
        }
      }
    }
  }
`;

const POST_BY_SLUG_QUERY = `
  query GetPostBySlug($slug: ID!) {
    post(id: $slug, idType: SLUG) {
      databaseId
      slug
      title
      excerpt
      content
      date
      featuredImage {
        node {
          sourceUrl
          altText
        }
      }
      categories {
        nodes {
          name
        }
      }
      author {
        node {
          name
        }
      }
    }
  }
`;

// Projects Query - Uses aliased snake_case field names to match WordPress ACF schema
const PROJECTS_QUERY = `
  query GetProjects {
    projects(first: 100) {
      nodes {
        databaseId
        slug
        title
        content
        featuredImage {
          node {
            sourceUrl
            altText
          }
        }
        projectFields {
          videoUrl
          category
          imageUrl
          projectLink
          statLabel
          statValue
          featured
        }
      }
    }
  }
`;

// Project by Slug Query - Uses aliased snake_case field names to match WordPress ACF schema
const PROJECT_BY_SLUG_QUERY = `
  query GetProjectBySlug($slug: ID!) {
    project(id: $slug, idType: SLUG) {
      databaseId
      slug
      title
      content
      featuredImage {
        node {
          sourceUrl
          altText
        }
      }
      projectFields {
        videoUrl
        category
        imageUrl
        projectLink
        statLabel
        statValue
        featured
      }
    }
  }
`;

const TESTIMONIALS_QUERY = `
  query GetTestimonials {
    testimonials(first: 100) {
      nodes {
        databaseId
        content
        testimonialFields {
          name
          role
          company
          rating
          quote
          photo {
            node {
              sourceUrl
            }
          }
          displayorder
        }
      }
    }
  }
`;

const TEAM_MEMBERS_QUERY = `
  query GetTeamMembers {
    teamMembers(first: 100) {
      nodes {
        databaseId
        title
        content
        featuredImage {
          node {
            sourceUrl
            altText
          }
        }
        teamMemberFields {
          role
          linkedinUrl
          teamType
          displayOrder
        }
      }
    }
  }
`;

const EVENTS_QUERY = `
  query GetEvents {
    events(first: 100) {
      nodes {
        databaseId
        title
        eventFields {
          tagline
          ctatext
          youtubeurl
          displayorder
        }
      }
    }
  }
`;

// Hero Settings Query - now fetches from Homepage page fields
const HERO_SETTINGS_QUERY = `
  query GetHeroSettings {
    pages(where: {name: "home"}, first: 1) {
      nodes {
        homepageFields {
        heroHeadline
        heroSubheadline
        heroCtaPrimary
        heroCtaSecondary
        heroCtaPrimaryUrl
        heroCtaPrimaryTarget
        heroCtaSecondaryUrl
        heroCtaSecondaryTarget
        heroVideoUrl
        heroVideoPoster {
          node {
            sourceUrl
          }
        }
        heroOverlayColor
        heroOverlayOpacity
        }
      }
    }
  }
`;

// Contact Settings Query - fetches contact page content from Site Settings CPT
const CONTACT_SETTINGS_QUERY = `
  query GetContactSettings {
    siteSettings(first: 20, where: { orderby: { field: MODIFIED, order: DESC } }) {
      nodes {
        databaseId
        siteSettingsContact {
          contactHeroTitle
          contactHeroSubtitle
          contactHeroImage { node { sourceUrl } }
          contactSectionTitle
          contactSectionDescription
          contactAddress
          contactEmail
          contactPhone
          contactOffices
          contactFormRecipients
          contactCareersTitle
          contactCareersText
        }
      }
    }
  }
`;

// Navigation Settings Query
const NAVIGATION_SETTINGS_QUERY = `
  query GetNavigationSettings {
    siteSettings(first: 20, where: { orderby: { field: MODIFIED, order: DESC } }) {
      nodes {
        databaseId
        siteSettingsNavigation {
          navItems
          navCtaLabel
          navCtaLink
        }
      }
    }
  }
`;

// Footer Settings Query
const FOOTER_SETTINGS_QUERY = `
  query GetFooterSettings {
    siteSettings(first: 20, where: { orderby: { field: MODIFIED, order: DESC } }) {
      nodes {
        databaseId
        siteSettingsFooter {
          footerCompanyName
          footerTagline
          footerCopyright
          footerLinks
        }
      }
    }
  }
`;

// Mega Navigation Settings Query
const MEGA_NAV_SETTINGS_QUERY = `
  query GetMegaNavSettings {
    siteSettings(first: 20, where: { orderby: { field: MODIFIED, order: DESC } }) {
      nodes {
        databaseId
        siteSettingsMegaNav {
          expHeadline
          expDescription
          expCtaLabel
          expCtaLink
          expFeaturedImage {
            node {
              sourceUrl
            }
          }
          expItem1Title
          expItem1Desc
          expItem1Icon
          expItem1Link
          expItem1External
          expItem2Title
          expItem2Desc
          expItem2Icon
          expItem2Link
          expItem2External
          expItem3Title
          expItem3Desc
          expItem3Icon
          expItem3Link
          expItem3External
          expItem4Title
          expItem4Desc
          expItem4Icon
          expItem4Link
          expItem4External
          expItem5Title
          expItem5Desc
          expItem5Icon
          expItem5Link
          expItem5External
          expItem6Title
          expItem6Desc
          expItem6Icon
          expItem6Link
          expItem6External
          vixiNavHeadline
          vixiNavDescription
          vixiNavCtaLabel
          vixiNavCtaLink
          vixiNavItem1Title
          vixiNavItem1Tagline
          vixiNavItem1Icon
          vixiNavItem1Link
          vixiNavItem2Title
          vixiNavItem2Tagline
          vixiNavItem2Icon
          vixiNavItem2Link
          vixiNavItem3Title
          vixiNavItem3Tagline
          vixiNavItem3Icon
          vixiNavItem3Link
          vixiNavItem4Title
          vixiNavItem4Tagline
          vixiNavItem4Icon
          vixiNavItem4Link
          vixiNavItem5Title
          vixiNavItem5Tagline
          vixiNavItem5Icon
          vixiNavItem5Link
          vixiNavItem6Title
          vixiNavItem6Tagline
          vixiNavItem6Icon
          vixiNavItem6Link
          workNavHeadline
          workNavDescription
          workNavCtaLabel
          workNavCtaLink
          workNavItem1Title
          workNavItem1Desc
          workNavItem1Icon
          workNavItem1Category
          workNavItem2Title
          workNavItem2Desc
          workNavItem2Icon
          workNavItem2Category
          workNavItem3Title
          workNavItem3Desc
          workNavItem3Icon
          workNavItem3Category
          workNavItem4Title
          workNavItem4Desc
          workNavItem4Icon
          workNavItem4Category
          workNavItem5Title
          workNavItem5Desc
          workNavItem5Icon
          workNavItem5Category
          workNavItem6Title
          workNavItem6Desc
          workNavItem6Icon
          workNavItem6Category
        }
      }
    }
  }
`

// Partner Logos Query - fetches all partner logo posts with ACF fields
const PARTNER_LOGOS_QUERY = `
  query GetPartnerLogos {
    partnerLogos(first: 100) {
      nodes {
        databaseId
        title
        featuredImage {
          node {
            sourceUrl
            altText
          }
        }
        partnerLogoFields {
          displayOrder
          row
          partnersLabel
          partnersTitle
          partnersTitleAccent
          partnersDescription
          isSettingsPost
        }
      }
    }
  }
`;

// ============================================
// GENERIC PAGE/POST BY URI QUERIES
// ============================================

const PAGE_BY_URI_QUERY = `
  query GetPageByUri($uri: String!) {
    pageBy(uri: $uri) {
      databaseId
      title
      content
      date
      featuredImage {
        node {
          sourceUrl
          altText
        }
      }
    }
  }
`;

const POST_BY_URI_QUERY = `
  query GetPostByUri($uri: String!) {
    postBy(uri: $uri) {
      databaseId
      slug
      title
      content
      excerpt
      date
      featuredImage {
        node {
          sourceUrl
          altText
        }
      }
      author {
        node {
          name
        }
      }
      categories {
        nodes {
          name
        }
      }
    }
  }
`;

// ============================================
// PAGE-LEVEL ACF FIELD QUERIES
// ============================================

const HOMEPAGE_FIELDS_QUERY = `
  query GetHomepageFields {
    pages(where: {name: "home"}, first: 1) {
      nodes {
        homepageFields {
        heroSectionEnabled
        workSnapshotsSectionEnabled
        teamMarqueeSectionEnabled
        vixieShowcaseSectionEnabled
        featuredEventsSectionEnabled
        trustLogosSectionEnabled
        globalMapSectionEnabled
        conversionCloseSectionEnabled
        blogSectionEnabled
        faqSectionEnabled
        linkedinFeedSectionEnabled
        testimonialsSectionEnabled
        vixieHeadline
        vixieMiniStat1Label
        vixieMiniStat1Value
        vixieMiniStat2Label
        vixieMiniStat2Value
        vixieMiniStat3Label
        vixieMiniStat3Value
        statCard1Value
        statCard1Suffix
        statCard1Title
        statCard1Description
        statCard2Value
        statCard2Suffix
        statCard2Title
        statCard2Description
        statCard3Value
        statCard3Suffix
        statCard3Title
        statCard3Description
        ctaHeadline
        ctaHeadlineAccent
        ctaSubheadline
        ctaButtonText
        ctaButtonUrl
        ctaButtonTarget
        ctaSecondaryButtonText
        ctaSecondaryButtonUrl
        ctaSecondaryButtonTarget
        heroHeadline
        heroSubheadline
        heroCtaPrimary
        heroCtaSecondary
        heroCtaPrimaryUrl
        heroCtaPrimaryTarget
        heroCtaSecondaryUrl
        heroCtaSecondaryTarget
        heroVideoUrl
        heroVideoPoster {
          node {
            sourceUrl
          }
        }
        heroOverlayColor
        heroOverlayOpacity
        mapHeadline
        mapHeadlineAccent
        mapDescription
        mapStat1Value
        mapStat1Suffix
        mapStat1Label
        mapStat2Value
        mapStat2Suffix
        mapStat2Label
        mapStat3Value
        mapStat3Suffix
        mapStat3Label
        mapStat4Value
        mapStat4Suffix
        mapStat4Label
        mapImage {
          node { sourceUrl }
        }
        mapOffice1Name
        mapOffice1X
        mapOffice1Y
        mapOffice2Name
        mapOffice2X
        mapOffice2Y
        mapOffice3Name
        mapOffice3X
        mapOffice3Y
        mapOffice4Name
        mapOffice4X
        mapOffice4Y
        mapOffice5Name
        mapOffice5X
        mapOffice5Y
        mapOffice6Name
        mapOffice6X
        mapOffice6Y
        mapOffice7Name
        mapOffice7X
        mapOffice7Y
        mapOffice8Name
        mapOffice8X
        mapOffice8Y
        mapTagline
        trustSectionHeading
        trustSectionSubtitle
        category1Name
        category1Subtitle
        category1VimeoId
        category2Name
        category2Subtitle
        category2VimeoId
        category3Name
        category3Subtitle
        category3VimeoId
        category4Name
        category4Subtitle
        category4VimeoId
        category5Name
        category5Subtitle
        category5VimeoId
        wwdItem1Label
        wwdItem1Title
        wwdItem1Description
        wwdItem1Highlight1
        wwdItem1Highlight2
        wwdItem1Highlight3
        wwdItem1Link
        wwdItem1Video
        wwdItem1ProjectName
        wwdItem1ProjectCategory
        wwdItem1ProjectImage {
          node { sourceUrl }
        }
        wwdItem2Label
        wwdItem2Title
        wwdItem2Description
        wwdItem2Highlight1
        wwdItem2Highlight2
        wwdItem2Highlight3
        wwdItem2Link
        wwdItem2Video
        wwdItem2ProjectName
        wwdItem2ProjectCategory
        wwdItem2ProjectImage {
          node { sourceUrl }
        }
        wwdItem3Label
        wwdItem3Title
        wwdItem3Description
        wwdItem3Highlight1
        wwdItem3Highlight2
        wwdItem3Highlight3
        wwdItem3Link
        wwdItem3Video
        wwdItem3ProjectName
        wwdItem3ProjectCategory
        wwdItem3ProjectImage {
          node { sourceUrl }
        }
        wwdItem4Label
        wwdItem4Title
        wwdItem4Description
        wwdItem4Highlight1
        wwdItem4Highlight2
        wwdItem4Highlight3
        wwdItem4Link
        wwdItem4Video
        wwdItem4ProjectName
        wwdItem4ProjectCategory
        wwdItem4ProjectImage {
          node { sourceUrl }
        }
        wwdItem5Label
        wwdItem5Title
        wwdItem5Description
        wwdItem5Highlight1
        wwdItem5Highlight2
        wwdItem5Highlight3
        wwdItem5Link
        wwdItem5Video
        wwdItem5ProjectName
        wwdItem5ProjectCategory
        wwdItem5ProjectImage {
          node { sourceUrl }
        }
        wwdItem6Label
        wwdItem6Title
        wwdItem6Description
        wwdItem6Highlight1
        wwdItem6Highlight2
        wwdItem6Highlight3
        wwdItem6Link
        wwdItem6Video
        wwdItem6ProjectName
        wwdItem6ProjectCategory
        wwdItem6ProjectImage {
          node { sourceUrl }
        }
        }
      }
    }
  }
`;

const COMPANY_PAGE_QUERY = `
  query GetCompanyPageFields {
    page(id: "company", idType: URI) {
      companyPageFields {
        companyHeroSectionEnabled
        companyPhotoGridSectionEnabled
        companyStatsSectionEnabled
        companyValuesSectionEnabled
        companyLeadershipSectionEnabled
        companyPresenceSectionEnabled
        companyCtaSectionEnabled
        heroTitle
        heroSubtitle
        heroDescription
        heroImage {
          node {
            sourceUrl
          }
        }
        missionStatement
        missionSubtext
        stat1Value
        stat1Label
        stat2Value
        stat2Label
        stat3Value
        stat3Label
        stat4Value
        stat4Label
        valuesSectionTitle
        valuesSectionSubtitle
        value1Title
        value1Description
        value2Title
        value2Description
        value3Title
        value3Description
        value4Title
        value4Description
        photoGridImage1 {
          node { sourceUrl }
        }
        photoGridImage2 {
          node { sourceUrl }
        }
        photoGridImage3 {
          node { sourceUrl }
        }
        photoGridImage4 {
          node { sourceUrl }
        }
        photoGridImage5 {
          node { sourceUrl }
        }
        photoGridAlt1
        photoGridAlt2
        photoGridAlt3
        photoGridAlt4
        photoGridAlt5
        globalPresenceTitle
        globalPresenceSubtitle
        globalPresenceCities
        ctaHeadline
        ctaSubtitle
        ctaButtonText
        ctaButtonUrl
        ctaButtonTarget
        companyAwardsSectionEnabled
        companyAwardsTitle
        companyAwardsSubtitle
        companyAward1Image { node { sourceUrl } }
        companyAward1Title
        companyAward1Year
        companyAward1Link
        companyAward2Image { node { sourceUrl } }
        companyAward2Title
        companyAward2Year
        companyAward2Link
        companyAward3Image { node { sourceUrl } }
        companyAward3Title
        companyAward3Year
        companyAward3Link
        companyAward4Image { node { sourceUrl } }
        companyAward4Title
        companyAward4Year
        companyAward4Link
        companyAward5Image { node { sourceUrl } }
        companyAward5Title
        companyAward5Year
        companyAward5Link
        companyAward6Image { node { sourceUrl } }
        companyAward6Title
        companyAward6Year
        companyAward6Link
      }
    }
  }
`;

const SOLUTIONS_PAGE_QUERY = `
  query GetSolutionsPageFields {
    page(id: "solutions", idType: URI) {
      solutionsPageFieldsGroup {
        solutionsHeroSectionEnabled
        solutionsOfferingsSectionEnabled
        solutionsTeamMarqueeSectionEnabled
        solutionsWhySectionEnabled
        solutionsCtaSectionEnabled
        heroBadgeText
        heroTitleLine1
        heroTitleLine2
        heroDescription
        heroStat1Value
        heroStat1Label
        heroStat2Value
        heroStat2Label
        heroStat3Value
        heroStat3Label
        offering1Desc
        offering1Title
        offering1Subtitle
        offering1Vimeo
        offering1StatValue
        offering1StatLabel
        offering1Highlight1
        offering1Highlight2
        offering1Highlight3
        offering1Cta1Text
        offering1Cta1Link
        offering1Cta2Text
        offering1Cta2Link
        offering2Desc
        offering2Title
        offering2Subtitle
        offering2Vimeo
        offering2StatValue
        offering2Highlight1
        offering2Highlight2
        offering2Highlight3
        offering2Cta1Text
        offering2Cta1Link
        offering2Cta2Text
        offering2Cta2Link
        offering3Title
        offering3Subtitle
        offering3Desc
        offering3Vimeo
        offering3StatValue
        offering3StatLabel
        offering3Highlight1
        offering3Highlight2
        offering3Highlight3
        offering3Cta1Text
        offering3Cta1Link
        offering3Cta2Text
        offering3Cta2Link
        offering4Title
        offering4Subtitle
        offering4Desc
        offering4Vimeo
        offering4StatValue
        offering4StatLabel
        offering4Highlight1
        offering4Highlight2
        offering4Highlight3
        offering4Cta1Text
        offering4Cta1Link
        offering4Cta2Text
        offering4Cta2Link
        whyTitle
        whySubtitle
        whyCard1Title
        whyCard1Desc
        whyCard2Title
        whyCard2Desc
        whyCard3Title
        whyCard3Desc
        whyCard4Title
        whyCard4Desc
      }
    }
  }
`;

const WORK_PAGE_QUERY = `
  query GetWorkPageFields {
    page(id: "work", idType: URI) {
      workPageFields {
        workHeroTitle: work_hero_title
        workHeroSubtitle: work_hero_subtitle
      }
    }
  }
`;

// Patent Disclaimer Settings Query
const PATENT_DISCLAIMER_QUERY = `
  query GetPatentDisclaimerSettings {
    siteSettings(first: 20, where: { orderby: { field: MODIFIED, order: DESC } }) {
      nodes {
        databaseId
        siteSettingsPatentDisclaimer {
          patentDisclaimerText
        }
      }
    }
  }
`;

// Vixi Main Page Fields Query
const VIXI_MAIN_PAGE_QUERY = `
  query GetVixiMainPageFields {
    page(id: "vixi", idType: URI) {
      vixiMainPageFields {
        vixiHeroSectionEnabled
        vixiStatsSectionEnabled
        vixiModulesSectionEnabled
        vixiVixieShowcaseSectionEnabled
        vixiFanEngagementStatsSectionEnabled
        vixiLightshowHighlightSectionEnabled
        vixiTeamMarqueeSectionEnabled
        vixiTestimonialSectionEnabled
        vixiCtaSectionEnabled
        vixiPatentDisclaimerSectionEnabled
        vixiHeroLogoImage {
          node {
            sourceUrl
          }
        }
        vixiHeroSubtitle
        vixiHeroDescription
        vixiHeroCta1Text
        vixiHeroCta1Link
        vixiHeroCta2Text
        vixiHeroCta2Link
        vixiHeroBgVideo
        vixiVideoModalId
        vixiStatsHeadline
        vixiStatsSubtitle
        vixiStat1Value
        vixiStat1Label
        vixiStat2Value
        vixiStat2Label
        vixiStat3Value
        vixiStat3Label
        vixiStat4Value
        vixiStat4Label
        vixiModulesHeadline
        vixiModulesDescription
        vixiModule1Title
        vixiModule1Tagline
        vixiModule1Description
        vixiModule1Features
        vixiModule1VimeoId
        vixiModule1Icon
        vixiModule1Link
        vixiModule1Gradient
        vixiModule2Title
        vixiModule2Tagline
        vixiModule2Description
        vixiModule2Features
        vixiModule2VimeoId
        vixiModule2Icon
        vixiModule2Link
        vixiModule2Gradient
        vixiModule3Title
        vixiModule3Tagline
        vixiModule3Description
        vixiModule3Features
        vixiModule3VimeoId
        vixiModule3Icon
        vixiModule3Link
        vixiModule3Gradient
        vixiModule4Title
        vixiModule4Tagline
        vixiModule4Description
        vixiModule4Features
        vixiModule4VimeoId
        vixiModule4Icon
        vixiModule4Link
        vixiModule4Gradient
        vixiLsBadgeText
        vixiLsHeadline
        vixiLsDescription
        vixiLsStat1Title
        vixiLsStat1Subtitle
        vixiLsStat2Title
        vixiLsStat2Subtitle
        vixiLsStat3Title
        vixiLsStat3Subtitle
        vixiLsCtaText
        vixiLsCtaLink
        vixiLsBgVideo
        vixiTestimonialQuote
        vixiTestimonialAuthor
        vixiTestimonialTitle
        vixiTestimonialStars
        vixiCtaHeadline
        vixiCtaSubtitle
        vixiCtaCta1Text
        vixiCtaCta1Link
        vixiCtaCta2Text
        vixiCtaCta2Link
        vixiNavCtaText
        vixiNavCtaLink
        vixiNav1Label
        vixiNav1Path
        vixiNav1Description
        vixiNav1Gradient
        vixiNav1GlowColor
        vixiNav1Icon
        vixiNav2Label
        vixiNav2Path
        vixiNav2Description
        vixiNav2Gradient
        vixiNav2GlowColor
        vixiNav2Icon
        vixiNav3Label
        vixiNav3Path
        vixiNav3Description
        vixiNav3Gradient
        vixiNav3GlowColor
        vixiNav3Icon
        vixiNav4Label
        vixiNav4Path
        vixiNav4Description
        vixiNav4Gradient
        vixiNav4GlowColor
        vixiNav4Icon
        vixiNav5Label
        vixiNav5Path
        vixiNav5Description
        vixiNav5Gradient
        vixiNav5GlowColor
        vixiNav5Icon
        vixiNav6Label
        vixiNav6Path
        vixiNav6Description
        vixiNav6Gradient
        vixiNav6GlowColor
        vixiNav6Icon
        vixiNav7Label
        vixiNav7Path
        vixiNav7Description
        vixiNav7Gradient
        vixiNav7GlowColor
        vixiNav7Icon
        vixiFanMomentsHeadline
        vixiFanMomentsHashtag
        vixiFanMomentsStat1Value
        vixiFanMomentsStat1Label
        vixiFanMomentsStat2Value
        vixiFanMomentsStat2Label
        vixiFanMomentsStat3Value
        vixiFanMomentsStat3Label
        vixiFanMomentsCtaText
        vixiFanMomentsCtaLink
        vixiFanMomentsCard1Label
        vixiFanMomentsCard1Tagline
        vixiFanMomentsCard1Media
        vixiFanMomentsCard2Label
        vixiFanMomentsCard2Tagline
        vixiFanMomentsCard2Media
        vixiFanMomentsCard3Label
        vixiFanMomentsCard3Tagline
        vixiFanMomentsCard3Media
        vixiEngagementHeadline
        vixiEngagementBadge
        vixiEngagementStat1Value
        vixiEngagementStat1Suffix
        vixiEngagementStat1Title
        vixiEngagementStat1Description
        vixiEngagementStat2Value
        vixiEngagementStat2Suffix
        vixiEngagementStat2Title
        vixiEngagementStat2Description
        vixiEngagementStat3Value
        vixiEngagementStat3Suffix
        vixiEngagementStat3Title
        vixiEngagementStat3Description
        vixiEngagementCtaText
        vixiEngagementCtaLink
      }
    }
  }
`;

// ============================================
// FETCH FUNCTIONS
// ============================================

export async function fetchFAQs(): Promise<FAQ[]> {
  const data = await graphqlRequest<GQLFAQsResponse>(FAQS_QUERY);
  return data.faqs.nodes.map(transformGQLFAQ);
}

export async function fetchPosts(params?: {
  page?: number;
  perPage?: number;
}): Promise<BlogPost[]> {
  const first = params?.perPage || 10;
  const data = await graphqlRequest<GQLPostsResponse>(POSTS_QUERY, { first });
  return data.posts.nodes.map(transformGQLPost);
}

export async function fetchPost(slug: string): Promise<BlogPost> {
  const data = await graphqlRequest<{ post: GQLPostNode | null }>(POST_BY_SLUG_QUERY, { slug });
  if (!data.post) throw new Error('Post not found');
  return transformGQLPost(data.post);
}

export async function fetchProjects(): Promise<Project[]> {
  const data = await graphqlRequest<GQLProjectsResponse>(PROJECTS_QUERY);
  return data.projects.nodes.map(transformGQLProject);
}

export async function fetchProject(slug: string): Promise<Project> {
  const data = await graphqlRequest<{ project: GQLProjectNode | null }>(PROJECT_BY_SLUG_QUERY, { slug });
  if (!data.project) throw new Error('Project not found');
  return transformGQLProject(data.project);
}

export async function fetchTestimonials(): Promise<Testimonial[]> {
  const data = await graphqlRequest<GQLTestimonialsResponse>(TESTIMONIALS_QUERY);
  return data.testimonials.nodes.map(transformGQLTestimonial);
}

export async function fetchTeamMembers(): Promise<TeamMember[]> {
  const data = await graphqlRequest<GQLTeamMembersResponse>(TEAM_MEMBERS_QUERY);
  return data.teamMembers.nodes.map(transformGQLTeamMember).sort((a, b) => (a.order || 0) - (b.order || 0));
}

export async function fetchEvents(): Promise<FeaturedEvent[]> {
  const data = await graphqlRequest<GQLEventsResponse>(EVENTS_QUERY);
  return data.events.nodes.map(transformGQLEvent);
}

export async function fetchHeroSettings(): Promise<HeroSettings | null> {
  const data = await graphqlRequest<GQLHeroSettingsResponse>(HERO_SETTINGS_QUERY);
  const fields = data.pages?.nodes?.[0]?.homepageFields;
  console.log('[WordPress] Hero settings from homepage:', { hasFields: !!fields, headline: fields?.heroHeadline });
  if (!fields) return null;
  const result: HeroSettings = {
    headline: decodeHtmlEntities(fields.heroHeadline || ''),
    subheadline: decodeHtmlEntities(fields.heroSubheadline || ''),
    ctaPrimary: fields.heroCtaPrimary || '',
    ctaSecondary: fields.heroCtaSecondary || '',
    ctaPrimaryUrl: fields.heroCtaPrimaryUrl || undefined,
    ctaPrimaryTarget: fields.heroCtaPrimaryTarget || undefined,
    ctaSecondaryUrl: fields.heroCtaSecondaryUrl || undefined,
    ctaSecondaryTarget: fields.heroCtaSecondaryTarget || undefined,
    videoUrl: fields.heroVideoUrl,
    videoPoster: fields.heroVideoPoster?.node?.sourceUrl,
    heroOverlayColor: fields.heroOverlayColor || undefined,
    heroOverlayOpacity: fields.heroOverlayOpacity != null ? Number(fields.heroOverlayOpacity) : undefined,
  };
  console.log('[WordPress] Hero settings loaded:', result);
  return result;
}

/**
 * Picks the best Site Settings node for a given ACF field group.
 * Priority:
 *   1. The most-recent node where the group has actual populated content (per `hasContent`)
 *   2. The most-recent node that at least has the group object
 *   3. The first node in the list
 *   4. null
 * This avoids the regression where a stale/empty Site Settings post (e.g. an autosave
 * or duplicate entry that was modified last) wipes out global content like footer/disclaimer.
 */
function pickBestNode<N extends Record<string, any>, K extends keyof N>(
  nodes: N[] | undefined | null,
  groupKey: K,
  hasContent: (group: N[K]) => boolean,
): N | undefined {
  if (!nodes || nodes.length === 0) return undefined;
  const populated = nodes.find((n) => n && n[groupKey] && hasContent(n[groupKey]));
  if (populated) return populated;
  const present = nodes.find((n) => n && n[groupKey]);
  if (present) return present;
  return nodes[0];
}

export async function fetchContactSettings(): Promise<ContactSettings | null> {
  const data = await graphqlRequest<GQLContactSettingsResponse>(CONTACT_SETTINGS_QUERY);
  const node = pickBestNode(data.siteSettings?.nodes, 'siteSettingsContact', (g) =>
    !!(g?.contactHeroTitle || g?.contactSectionTitle || g?.contactEmail || g?.contactAddress)
  );
  if (!node?.siteSettingsContact) return null;
  return transformGQLContactSettings(node);
}

export async function fetchNavigationSettings(): Promise<NavigationSettings | null> {
  const data = await graphqlRequest<GQLNavigationSettingsResponse>(NAVIGATION_SETTINGS_QUERY);
  const node = pickBestNode(data.siteSettings?.nodes, 'siteSettingsNavigation', (g) =>
    !!((g?.navItems && g.navItems.trim()) || g?.navCtaLabel || g?.navCtaLink)
  );
  if (!node?.siteSettingsNavigation) return null;
  return transformGQLNavigationSettings(node);
}

export async function fetchFooterSettings(): Promise<FooterSettings | null> {
  const data = await graphqlRequest<GQLFooterSettingsResponse>(FOOTER_SETTINGS_QUERY);
  const node = pickBestNode(data.siteSettings?.nodes, 'siteSettingsFooter', (g) =>
    !!(g?.footerCompanyName || g?.footerTagline || g?.footerCopyright || (g?.footerLinks && String(g.footerLinks).trim()))
  );
  if (!node?.siteSettingsFooter) return null;
  return transformGQLFooterSettings(node);
}

export async function fetchPartnerLogos(): Promise<{ logos: PartnerLogo[]; settings: PartnersSectionSettings | null }> {
  const data = await graphqlRequest<GQLPartnerLogosResponse>(PARTNER_LOGOS_QUERY);
  const nodes = data.partnerLogos.nodes;

  // Separate settings post from logo posts
  const settingsNode = nodes.find(n => n.partnerLogoFields?.isSettingsPost === '1');
  const logoNodes = nodes.filter(n => n.partnerLogoFields?.isSettingsPost !== '1');

  const settings: PartnersSectionSettings | null = settingsNode?.partnerLogoFields ? {
    label: decodeHtmlEntities(settingsNode.partnerLogoFields.partnersLabel || ''),
    title: decodeHtmlEntities(settingsNode.partnerLogoFields.partnersTitle || ''),
    titleAccent: decodeHtmlEntities(settingsNode.partnerLogoFields.partnersTitleAccent || ''),
    description: decodeHtmlEntities(settingsNode.partnerLogoFields.partnersDescription || ''),
  } : null;

  const logos = logoNodes
    .map(transformGQLPartnerLogo)
    .sort((a, b) => a.order - b.order);

  return { logos, settings };
}

export async function fetchPatentDisclaimerSettings(): Promise<PatentDisclaimerSettings | null> {
  try {
    const data = await graphqlRequest<GQLPatentDisclaimerResponse>(PATENT_DISCLAIMER_QUERY);
    const node = pickBestNode(data.siteSettings?.nodes, 'siteSettingsPatentDisclaimer', (g) =>
      !!(g?.patentDisclaimerText && String(g.patentDisclaimerText).trim())
    );
    const text = node?.siteSettingsPatentDisclaimer?.patentDisclaimerText;
    if (!text) return null;
    return { text };
  } catch (e) {
    console.warn('[WordPress] Failed to fetch patent disclaimer settings:', e);
    return null;
  }
}

// Page-level field fetchers
export async function fetchHomepageFields(): Promise<HomepageFields | null> {
  try {
    const data = await graphqlRequest<GQLHomepageFieldsResponse>(HOMEPAGE_FIELDS_QUERY);
    const fields = data.pages?.nodes?.[0]?.homepageFields;
    if (!fields) return null;
    // Extract image URLs from wwd project image fields
    const result = { ...fields } as Record<string, unknown>;
    for (let i = 1; i <= 6; i++) {
      const imgKey = `wwdItem${i}ProjectImage`;
      const imgField = result[imgKey] as { node?: { sourceUrl?: string } } | undefined;
      if (imgField && typeof imgField === 'object') {
        result[imgKey] = imgField?.node?.sourceUrl || undefined;
      }
    }
    // Extract map image URL
    const mapImg = result.mapImage as { node?: { sourceUrl?: string } } | undefined;
    if (mapImg && typeof mapImg === 'object') {
      result.mapImage = mapImg?.node?.sourceUrl || undefined;
    }
    // Extract hero video poster URL
    const heroPoster = result.heroVideoPoster as { node?: { sourceUrl?: string } } | string | undefined;
    if (heroPoster && typeof heroPoster === 'object') {
      result.heroVideoPoster = (heroPoster as any)?.node?.sourceUrl || undefined;
    }
    return result as unknown as HomepageFields;
  } catch (e) {
    console.warn('[WordPress] Failed to fetch homepage fields:', e);
    return null;
  }
}

export async function fetchMegaNavSettings(): Promise<MegaNavSettings | null> {
  try {
    const data = await graphqlRequest<GQLMegaNavSettingsResponse>(MEGA_NAV_SETTINGS_QUERY);
    const nodes = data.siteSettings?.nodes;
    if (!nodes || nodes.length === 0) return null;

    // Per-group selection: each tab independently picks the most-complete record.
    // For experiences, prefer a node that has BOTH text AND a featured image,
    // so a stale text-only record can't blank out the image (and vice-versa).
    const pickGroup = (
      preferFull: (g: any) => boolean,
      hasAny: (g: any) => boolean,
    ): any | undefined => {
      const full = nodes.find((n: any) => n?.siteSettingsMegaNav && preferFull(n.siteSettingsMegaNav));
      if (full) return full.siteSettingsMegaNav;
      const any = nodes.find((n: any) => n?.siteSettingsMegaNav && hasAny(n.siteSettingsMegaNav));
      return any?.siteSettingsMegaNav;
    };

    const expGroup = pickGroup(
      (g) => !!((g?.expHeadline || g?.expItem1Title) && g?.expFeaturedImage?.node?.sourceUrl),
      (g) => !!(g?.expHeadline || g?.expItem1Title || g?.expFeaturedImage?.node?.sourceUrl),
    );
    const vixiGroup = pickGroup(
      (g) => !!(g?.vixiNavHeadline && g?.vixiNavItem1Title),
      (g) => !!(g?.vixiNavHeadline || g?.vixiNavItem1Title),
    );
    const workGroup = pickGroup(
      (g) => !!(g?.workNavHeadline && g?.workNavItem1Title),
      (g) => !!(g?.workNavHeadline || g?.workNavItem1Title),
    );

    if (!expGroup && !vixiGroup && !workGroup) return null;

    // Merge: each group's fields come from its own selected node.
    // CRITICAL: only copy keys that belong to this group's prefix, otherwise
    // a sibling group's null fields (e.g. vixi node's expFeaturedImage:null)
    // will overwrite the image we just selected from the experiences node.
    const pickPrefix = (g: any, prefixes: string[]): Record<string, unknown> => {
      if (!g) return {};
      const out: Record<string, unknown> = {};
      for (const k of Object.keys(g)) {
        if (prefixes.some((p) => k.startsWith(p))) out[k] = g[k];
      }
      return out;
    };
    const merged: Record<string, unknown> = {
      ...pickPrefix(expGroup, ['exp']),
      ...pickPrefix(vixiGroup, ['vixiNav']),
      ...pickPrefix(workGroup, ['workNav']),
    };

    if (typeof window !== 'undefined' && (expGroup || vixiGroup || workGroup)) {
      const idOf = (g: any) => nodes.find((n: any) => n?.siteSettingsMegaNav === g)?.databaseId;
      console.debug('[MegaNav] picked nodes — exp:', idOf(expGroup), 'vixi:', idOf(vixiGroup), 'work:', idOf(workGroup));
    }

    return transformMegaNavSettings(merged as Record<string, unknown>);
  } catch (e) {
    console.warn('[WordPress] Failed to fetch mega nav settings:', e);
    return null;
  }
}

function transformMegaNavSettings(f: Record<string, unknown>): MegaNavSettings {
  const buildItems = (prefix: string, count: number, descKey: string, extraKey?: string): MegaNavItem[] => {
    const items: MegaNavItem[] = [];
    for (let i = 1; i <= count; i++) {
      const title = ((f[`${prefix}${i}Title`] as string) || '').trim();
      if (!title) continue;
      items.push({
        title,
        description: (f[`${prefix}${i}${descKey}`] as string) || '',
        icon: (f[`${prefix}${i}Icon`] as string) || '',
        link: (f[`${prefix}${i}Link`] as string) || (f[`${prefix}${i}${extraKey || 'Link'}`] as string) || '',
        external: !!f[`${prefix}${i}External`] && f[`${prefix}${i}External`] !== '0',
        category: extraKey === 'Category' ? (f[`${prefix}${i}Category`] as string) : undefined,
      });
    }
    return items;
  };

  const expImg = f.expFeaturedImage as { node?: { sourceUrl?: string } } | undefined;

  return {
    experiences: {
      headline: (f.expHeadline as string) || '',
      description: (f.expDescription as string) || '',
      ctaLabel: (f.expCtaLabel as string) || '',
      ctaLink: (f.expCtaLink as string) || '',
      featuredImage: expImg?.node?.sourceUrl || '',
      items: buildItems('expItem', 6, 'Desc'),
    },
    vixi: {
      headline: (f.vixiNavHeadline as string) || '',
      description: (f.vixiNavDescription as string) || '',
      ctaLabel: (f.vixiNavCtaLabel as string) || '',
      ctaLink: (f.vixiNavCtaLink as string) || '',
      
      items: buildItems('vixiNavItem', 6, 'Tagline'),
    },
    work: {
      headline: (f.workNavHeadline as string) || '',
      description: (f.workNavDescription as string) || '',
      ctaLabel: (f.workNavCtaLabel as string) || '',
      ctaLink: (f.workNavCtaLink as string) || '',
      
      items: buildItems('workNavItem', 6, 'Desc', 'Category'),
    },
  };
}

export async function fetchCompanyPageFields(): Promise<CompanyPageFields | null> {
  try {
    const data = await graphqlRequest<GQLCompanyPageFieldsResponse>(COMPANY_PAGE_QUERY);
    const fields = data.page?.companyPageFields;
    if (!fields) return null;
    // Extract heroImage URL from media field
    const heroImageRaw = fields.heroImage as { node?: { sourceUrl?: string } } | undefined;
    const extractImageUrl = (field: unknown): string | undefined => {
      const img = field as { node?: { sourceUrl?: string } } | undefined;
      return img?.node?.sourceUrl || undefined;
    };
    return {
      ...fields,
      heroImage: heroImageRaw?.node?.sourceUrl || undefined,
      photoGridImage1: extractImageUrl(fields.photoGridImage1),
      photoGridImage2: extractImageUrl(fields.photoGridImage2),
      photoGridImage3: extractImageUrl(fields.photoGridImage3),
      photoGridImage4: extractImageUrl(fields.photoGridImage4),
      photoGridImage5: extractImageUrl(fields.photoGridImage5),
      // Awards
      companyAwardsSectionEnabled: fields.companyAwardsSectionEnabled as boolean | undefined,
      awardsTitle: fields.companyAwardsTitle as string | undefined,
      awardsSubtitle: fields.companyAwardsSubtitle as string | undefined,
      awards: (() => {
        const slots: Array<{ image?: string; title?: string; year?: string; link?: string }> = [];
        for (let i = 1; i <= 6; i++) {
          const title = fields[`companyAward${i}Title`] as string | undefined;
          const image = (fields[`companyAward${i}Image`] as { node?: { sourceUrl?: string } })?.node?.sourceUrl;
          const year = fields[`companyAward${i}Year`] as string | undefined;
          const link = fields[`companyAward${i}Link`] as string | undefined;
          if (title || image) {
            slots.push({ image: image || undefined, title: title || undefined, year: year || undefined, link: link || undefined });
          }
        }
        return slots.length > 0 ? slots : undefined;
      })(),
    } as unknown as CompanyPageFields;
  } catch (e) {
    console.warn('[WordPress] Failed to fetch company fields:', e);
    return null;
  }
}

export async function fetchSolutionsPageFields(): Promise<SolutionsPageFields | null> {
  try {
    const data = await graphqlRequest<GQLSolutionsPageFieldsResponse>(SOLUTIONS_PAGE_QUERY);
    const f = data.page?.solutionsPageFieldsGroup;
    if (!f) return null;
    // Normalize inconsistent field names to camelCase
    return {
      heroBadgeText: f.heroBadgeText as string,
      heroTitleLine1: f.heroTitleLine1 as string,
      heroTitleLine2: f.heroTitleLine2 as string,
      heroDescription: f.heroDescription as string,
      heroStat1Value: f.heroStat1Value as string,
      heroStat1Label: f.heroStat1Label as string,
      heroStat2Value: f.heroStat2Value as string,
      heroStat2Label: f.heroStat2Label as string,
      heroStat3Value: f.heroStat3Value as string,
      heroStat3Label: f.heroStat3Label as string,
      offering1Title: f.offering1Title as string,
      offering1Subtitle: f.offering1Subtitle as string,
      offering1Desc: f.offering1Desc as string,
      offering1Vimeo: f.offering1Vimeo as string,
      offering1StatValue: f.offering1StatValue as string,
      offering1StatLabel: f.offering1StatLabel as string,
      offering1Highlight1: f.offering1Highlight1 as string,
      offering1Highlight2: f.offering1Highlight2 as string,
      offering1Highlight3: f.offering1Highlight3 as string,
      offering2Title: f.offering2Title as string,
      offering2Subtitle: f.offering2Subtitle as string,
      offering2Desc: f.offering2Desc as string,
      offering2Vimeo: f.offering2Vimeo as string,
      offering2StatValue: f.offering2StatValue as string,
      offering2Highlight1: f.offering2Highlight1 as string,
      offering2Highlight2: f.offering2Highlight2 as string,
      offering2Highlight3: f.offering2Highlight3 as string,
      offering3Title: f.offering3Title as string,
      offering3Subtitle: f.offering3Subtitle as string,
      offering3Desc: f.offering3Desc as string,
      offering3Vimeo: f.offering3Vimeo as string,
      offering3StatValue: f.offering3StatValue as string,
      offering3StatLabel: f.offering3StatLabel as string,
      offering3Highlight1: f.offering3Highlight1 as string,
      offering3Highlight2: f.offering3Highlight2 as string,
      offering3Highlight3: f.offering3Highlight3 as string,
      offering4Title: f.offering4Title as string,
      offering4Subtitle: f.offering4Subtitle as string,
      offering4Desc: f.offering4Desc as string,
      offering4Vimeo: f.offering4Vimeo as string,
      offering4StatValue: f.offering4StatValue as string,
      offering4StatLabel: f.offering4StatLabel as string,
      offering4Highlight1: f.offering4Highlight1 as string,
      offering4Highlight2: f.offering4Highlight2 as string,
      offering4Highlight3: f.offering4Highlight3 as string,
      whyTitle: f.whyTitle as string,
      whySubtitle: f.whySubtitle as string,
      whyCard1Title: f.whyCard1Title as string,
      whyCard1Desc: f.whyCard1Desc as string,
      whyCard2Title: f.whyCard2Title as string,
      whyCard2Desc: f.whyCard2Desc as string,
      whyCard3Title: f.whyCard3Title as string,
      whyCard3Desc: f.whyCard3Desc as string,
      whyCard4Title: f.whyCard4Title as string,
      whyCard4Desc: f.whyCard4Desc as string,
    } as SolutionsPageFields;
  } catch (e) {
    console.warn('[WordPress] Failed to fetch solutions fields:', e);
    return null;
  }
}

export async function fetchWorkPageFields(): Promise<WorkPageFields | null> {
  try {
    const data = await graphqlRequest<GQLWorkPageFieldsResponse>(WORK_PAGE_QUERY);
    const fields = data.page?.workPageFields;
    if (!fields) return null;
    const f = fields as Record<string, unknown>;
    return {
      heroTitle: f.workHeroTitle as string || '',
      heroSubtitle: f.workHeroSubtitle as string || '',
    };
  } catch (e) {
    console.warn('[WordPress] Failed to fetch work fields:', e);
    return null;
  }
}

function transformGQLPartnerLogo(node: GQLPartnerLogosResponse['partnerLogos']['nodes'][0]): PartnerLogo {
  return {
    id: node.databaseId,
    name: decodeHtmlEntities(node.title),
    logo: node.featuredImage?.node?.sourceUrl || '',
    row: Array.isArray(node.partnerLogoFields?.row) ? node.partnerLogoFields.row[0] || '1' : node.partnerLogoFields?.row || '1',
    order: node.partnerLogoFields?.displayOrder || 0,
  };
}

// ============================================
// TRANSFORMERS (GraphQL → Component format)
// ============================================

function transformGQLFAQ(node: GQLFAQsResponse['faqs']['nodes'][0]): FAQ {
  // Try ACF answer field first, then fall back to content
  const rawAnswer = node.faqFields?.answer || node.content || '';
  
  return {
    question: decodeHtmlEntities(node.faqFields?.field1Question || node.title),
    answer: rawAnswer.replace(/<\/?p>/g, '').trim(),
    order: node.faqFields?.displayOrder || 0,
  };
}

function transformGQLPost(node: GQLPostNode): BlogPost {
  // Estimate read time: ~200 words per minute
  const wordCount = node.content.replace(/<[^>]*>/g, '').split(/\s+/).length;
  const readTime = `${Math.max(1, Math.ceil(wordCount / 200))} min read`;

  return {
    id: node.databaseId,
    slug: node.slug,
    title: decodeHtmlEntities(node.title),
    excerpt: decodeHtmlEntities(node.excerpt.replace(/<[^>]*>/g, '').trim()),
    content: node.content,
    date: new Date(node.date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }),
    image: node.featuredImage?.node.sourceUrl || '/placeholder.svg',
    imageAlt: node.featuredImage?.node.altText || node.title,
    category: node.categories.nodes[0]?.name || 'Uncategorized',
    author: node.author.node.name || 'The Famous Group',
    readTime,
  };
}

function transformGQLProject(node: GQLProjectNode): Project {
  // Fallback chain: Featured Image → ACF image_url → Video Thumbnail → Placeholder
  const featuredImage = node.featuredImage?.node.sourceUrl;
  const acfImage = node.projectFields?.imageUrl;
  const videoThumbnail = node.projectFields?.videoUrl 
    ? getVideoThumbnail(node.projectFields.videoUrl) 
    : null;
  const imageUrl = featuredImage || acfImage || videoThumbnail || '/placeholder.svg';

  // Build stats string from stat_label + stat_value
  const statLabel = node.projectFields?.statLabel;
  const statValue = node.projectFields?.statValue;
  const stats = statLabel && statValue ? `${statValue} ${statLabel}` : statLabel || statValue || undefined;

  return {
    id: node.databaseId,
    slug: node.slug,
    title: decodeHtmlEntities(node.title),
    description: (node.content || '').replace(/<\/?p>/g, '').trim(),
    videoUrl: node.projectFields?.videoUrl,
    imageUrl,
    category: node.projectFields?.category || 'Mixed Reality',
    client: undefined,
    year: undefined,
    stats,
    externalLink: node.projectFields?.projectLink,
    featured: Boolean(node.projectFields?.featured),
  };
}

function transformGQLTestimonial(node: GQLTestimonialsResponse['testimonials']['nodes'][0]): Testimonial {
  return {
    id: node.databaseId,
    name: node.testimonialFields?.name || '',
    role: node.testimonialFields?.role || '',
    company: node.testimonialFields?.company || '',
    // Try ACF quote field first, then content, then empty string
    quote: (node.testimonialFields?.quote || node.content || '')
      .replace(/<\/?p>/g, '')
      .trim(),
    rating: Number(node.testimonialFields?.rating) || 5,
    image: node.testimonialFields?.photo?.node?.sourceUrl,
  };
}

function transformGQLTeamMember(node: GQLTeamMembersResponse['teamMembers']['nodes'][0]): TeamMember {
  return {
    id: node.databaseId,
    name: decodeHtmlEntities(node.title),
    role: node.teamMemberFields?.role || '',
    bio: (node.content || '').replace(/<\/?p>/g, '').trim(),
    image: node.featuredImage?.node.sourceUrl || '/placeholder.svg',
    linkedIn: node.teamMemberFields?.linkedinUrl,
    teamType: (() => { const raw = node.teamMemberFields?.teamType; return Array.isArray(raw) ? raw[0] : (raw || 'leadership'); })(),
    order: node.teamMemberFields?.displayOrder || 0,
  };
}

function transformGQLEvent(node: GQLEventsResponse['events']['nodes'][0]): FeaturedEvent {
  return {
    id: node.databaseId,
    title: decodeHtmlEntities(node.title),
    tagline: node.eventFields?.tagline || '',
    ctaText: node.eventFields?.ctatext || 'View Case Study',
    youtubeUrl: node.eventFields?.youtubeurl || '',
    order: node.eventFields?.displayorder || 0,
  };
}

// transformGQLHeroSettings removed — hero settings are now fetched inline in fetchHeroSettings()

function transformGQLContactSettings(node: GQLContactSettingsResponse['siteSettings']['nodes'][0]): ContactSettings {
  const fields = node.siteSettingsContact;
  // Parse offices from newline-separated string to array
  const officesRaw = fields?.contactOffices || '';
  const offices = officesRaw
    .split('\n')
    .map((o: string) => o.trim())
    .filter((o: string) => o.length > 0);

  return {
    heroTitle: decodeHtmlEntities(fields?.contactHeroTitle || ''),
    heroSubtitle: decodeHtmlEntities(fields?.contactHeroSubtitle || ''),
    sectionTitle: decodeHtmlEntities(fields?.contactSectionTitle || ''),
    sectionDescription: decodeHtmlEntities(fields?.contactSectionDescription || ''),
    address: decodeHtmlEntities(fields?.contactAddress || ''),
    email: fields?.contactEmail || '',
    phone: fields?.contactPhone || '',
    offices,
    formRecipients: (fields?.contactFormRecipients || '')
      .split(',')
      .map((e: string) => e.trim())
      .filter((e: string) => e.length > 0),
    careersTitle: decodeHtmlEntities(fields?.contactCareersTitle || ''),
    careersText: decodeHtmlEntities(fields?.contactCareersText || ''),
    heroImage: fields?.contactHeroImage?.node?.sourceUrl || '',
  };
}

function transformGQLNavigationSettings(node: GQLNavigationSettingsResponse['siteSettings']['nodes'][0]): NavigationSettings {
  const fields = node.siteSettingsNavigation;
  const rawItems = fields?.navItems || '';
  const items = parsePipeDelimitedList(rawItems).map(({ left, right, third }) => ({
    label: left,
    path: right,
    external: third?.toLowerCase() === 'external',
  }));

  return {
    items,
    ctaLabel: decodeHtmlEntities(fields?.navCtaLabel || ''),
    ctaLink: fields?.navCtaLink || '',
  };
}

function transformGQLFooterSettings(node: GQLFooterSettingsResponse['siteSettings']['nodes'][0]): FooterSettings {
  const fields = node.siteSettingsFooter;
  const rawLinks = fields?.footerLinks || '';
  const links = parsePipeDelimitedList(rawLinks).map(({ left, right }) => ({
    label: left,
    url: right,
  }));

  return {
    companyName: decodeHtmlEntities(fields?.footerCompanyName || ''),
    tagline: decodeHtmlEntities(fields?.footerTagline || ''),
    copyright: decodeHtmlEntities(fields?.footerCopyright || ''),
    links,
  };
}

// ============================================
// HELPERS
// ============================================

/** Parse a newline-separated, pipe-delimited textarea value into pairs (supports optional 3rd segment) */
function parsePipeDelimitedList(raw: string): Array<{ left: string; right: string; third?: string }> {
  return raw
    .split('\n')
    .map(line => line.trim())
    .filter(line => line.includes('|'))
    .map(line => {
      const parts = line.split('|').map(s => s.trim());
      return { left: parts[0], right: parts[1], third: parts[2] };
    });
}

// ============================================

function decodeHtmlEntities(text: string): string {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
}

// ============================================
// GENERIC PAGE / POST BY URI FETCHERS
// ============================================

export async function fetchPageByUri(uri: string): Promise<WPPageContent | null> {
  try {
    const data = await graphqlRequest<{ pageBy: { databaseId: number; title: string; content: string; date: string; featuredImage?: { node: { sourceUrl: string; altText?: string } } } | null }>(
      PAGE_BY_URI_QUERY,
      { uri }
    );
    if (!data.pageBy) return null;
    const p = data.pageBy;
    return {
      id: p.databaseId,
      title: decodeHtmlEntities(p.title || ''),
      content: p.content || '',
      date: p.date || '',
      featuredImage: p.featuredImage?.node?.sourceUrl || '',
      featuredImageAlt: p.featuredImage?.node?.altText || '',
    };
  } catch (err) {
    console.error('[WordPress] fetchPageByUri error:', err);
    return null;
  }
}

export async function fetchPostByUri(uri: string): Promise<BlogPost | null> {
  try {
    const data = await graphqlRequest<{ postBy: GQLPostNode | null }>(
      POST_BY_URI_QUERY,
      { uri }
    );
    if (!data.postBy) return null;
    return transformGQLPost(data.postBy);
  } catch (err) {
    console.error('[WordPress] fetchPostByUri error:', err);
    return null;
  }
}

// PAGE SECTION BUILDER — REMOVED
// Section visibility is now controlled by per-section toggle fields in each page's ACF field group.
// The DynamicPageRenderer reads these toggles from the existing page field queries.

// ============================================
// YOAST SEO
// ============================================

const YOAST_PAGE_QUERY = `
  query GetPageSEO($uri: String!) {
    pageBy(uri: $uri) {
      seo {
        title
        metaDesc
        canonical
        opengraphTitle
        opengraphDescription
        opengraphImage { sourceUrl }
        opengraphUrl
        twitterTitle
        twitterDescription
        twitterImage { sourceUrl }
        metaRobotsNoindex
        metaRobotsNofollow
        schema { raw }
      }
    }
  }
`;

const YOAST_POST_QUERY = `
  query GetPostSEO($uri: String!) {
    postBy(uri: $uri) {
      seo {
        title
        metaDesc
        canonical
        opengraphTitle
        opengraphDescription
        opengraphImage { sourceUrl }
        opengraphUrl
        twitterTitle
        twitterDescription
        twitterImage { sourceUrl }
        metaRobotsNoindex
        metaRobotsNofollow
        schema { raw }
      }
    }
  }
`;

export async function fetchYoastSEO(uri: string): Promise<YoastSEO | null> {
  try {
    // Try page first
    const pageData = await graphqlRequest<GQLYoastPageResponse>(YOAST_PAGE_QUERY, { uri });
    if (pageData.pageBy?.seo) {
      return pageData.pageBy.seo as YoastSEO;
    }

    // Try post
    const postData = await graphqlRequest<GQLYoastPostResponse>(YOAST_POST_QUERY, { uri });
    if (postData.postBy?.seo) {
      return postData.postBy.seo as YoastSEO;
    }

    return null;
  } catch (err) {
    console.warn('[WordPress] fetchYoastSEO error:', err);
    return null;
  }
}



export async function fetchVixiMainPageFields(): Promise<VixiMainPageFields | null> {
  try {
    const data = await graphqlRequest<GQLVixiMainPageFieldsResponse>(VIXI_MAIN_PAGE_QUERY);
    const f = data.page?.vixiMainPageFields;
    if (!f) return null;
    return f as unknown as VixiMainPageFields;
  } catch (e) {
    console.warn('[WordPress] Failed to fetch Vixi main page fields:', e);
    return null;
  }
}
// ============================================
// VIXI SUB-PAGE QUERIES & FETCHERS
// ============================================

const VIXI_CAPTURE_QUERY = `
  query GetVixiCaptureFields {
    page(id: "vixi/capture", idType: URI) {
      vixiCaptureFields {
        captureHeroBadge captureHeroTitle captureHeroSubtitle captureHeroDescription
        captureHeroCta1Text captureHeroCta1Link captureHeroCta2Text captureHeroCta2Link captureHeroBgVideo
        captureFeatHeadline captureFeatSubtitle
        captureFeat1Title captureFeat1Desc captureFeat1Gradient captureFeat1Vimeo captureFeat1Image { node { sourceUrl } } captureFeat1StatValue captureFeat1StatLabel captureFeat1Icon
        captureFeat2Title captureFeat2Desc captureFeat2Gradient captureFeat2Vimeo captureFeat2Image { node { sourceUrl } } captureFeat2StatValue captureFeat2StatLabel captureFeat2Icon
        captureFeat3Title captureFeat3Desc captureFeat3Gradient captureFeat3Vimeo captureFeat3Image { node { sourceUrl } } captureFeat3StatValue captureFeat3StatLabel captureFeat3Icon
        captureVideoHeadline captureVideoDesc captureVideoVimeo
        captureVideoCtaText captureVideoCtaLink
        captureCtaHeadline captureCtaDesc captureCtaCta1Text captureCtaCta1Link captureCtaCta2Text captureCtaCta2Link
        captureCtaTrust1 captureCtaTrust2 captureCtaTrust3
      }
    }
  }
`;

const VIXI_MAGIC_QUERY = `
  query GetVixiMagicFields {
    page(id: "vixi/magic", idType: URI) {
      vixiMagicFields {
        magicHeroBadge magicHeroTitle magicHeroSubtitle magicHeroDescription
        magicHeroCta1Text magicHeroCta1Link magicHeroCta2Text magicHeroCta2Link magicHeroBgVideo
        magicFeatHeadline magicFeatSubtitle
        magicFeat1Title magicFeat1Tagline magicFeat1Desc magicFeat1Gradient magicFeat1Vimeo magicFeat1Icon
        magicFeat2Title magicFeat2Tagline magicFeat2Desc magicFeat2Gradient magicFeat2Vimeo magicFeat2Icon
        magicFeat3Title magicFeat3Tagline magicFeat3Desc magicFeat3Gradient magicFeat3Vimeo magicFeat3Icon
        magicCtaHeadline magicCtaDesc magicCtaCta1Text magicCtaCta1Link
      }
    }
  }
`;

const VIXI_STREAMING_QUERY = `
  query GetVixiStreamingFields {
    page(id: "vixi/streaming", idType: URI) {
      vixiStreamingFields {
        streamingHeroBadge streamingHeroTitle streamingHeroSubtitle streamingHeroDescription
        streamingHeroCta1Text streamingHeroCta1Link streamingHeroCta2Text streamingHeroCta2Link streamingHeroBgVideo
        streamingSpec1Label streamingSpec1Value streamingSpec2Label streamingSpec2Value
        streamingSpec3Label streamingSpec3Value streamingSpec4Label streamingSpec4Value
        streamingFeatHeadline streamingFeatSubtitle
        streamingFeat1Title streamingFeat1Subtitle streamingFeat1Desc streamingFeat1Gradient streamingFeat1Vimeo streamingFeat1Icon
        streamingFeat2Title streamingFeat2Subtitle streamingFeat2Desc streamingFeat2Gradient streamingFeat2Vimeo streamingFeat2Icon
        streamingFeat3Title streamingFeat3Subtitle streamingFeat3Desc streamingFeat3Gradient streamingFeat3Vimeo streamingFeat3Icon
        streamingVideoHeadline streamingVideoDesc streamingVideoVimeo
        streamingVideoCtaText streamingVideoCtaLink
        streamingCtaHeadline streamingCtaDesc streamingCtaCta1Text streamingCtaCta1Link
        streamingCtaTrust1 streamingCtaTrust2 streamingCtaTrust3
      }
    }
  }
`;

const VIXI_LIGHTSHOW_QUERY = `
  query GetVixiLightshowFields {
    page(id: "vixi/lightshow", idType: URI) {
      vixiLightshowFields {
        lightshowHeroBadge lightshowHeroTitle1 lightshowHeroTitle2 lightshowHeroSubtitle lightshowHeroDescription
        lightshowHeroCta1Text lightshowHeroCta1Link lightshowHeroCta2Text lightshowHeroCta2Link lightshowHeroBgVideo
        lightshowStat1Value lightshowStat1Label lightshowStat2Value lightshowStat2Label
        lightshowStat3Value lightshowStat3Label lightshowStat4Value lightshowStat4Label
        lightshowHiwHeadline lightshowHiwSubtitle
        lightshowStep1Title lightshowStep1Desc lightshowStep2Title lightshowStep2Desc lightshowStep3Title lightshowStep3Desc
        lightshowPtHeadline lightshowPtSubtitle
        lightshowTool1Title lightshowTool1Desc lightshowTool1Gradient lightshowTool1Icon
        lightshowTool2Title lightshowTool2Desc lightshowTool2Gradient lightshowTool2Icon
        lightshowTool3Title lightshowTool3Desc lightshowTool3Gradient lightshowTool3Icon
        lightshowVideoHeadline lightshowVideoDesc lightshowVideoVimeo
        lightshowVideoCtaText lightshowVideoCtaLink
        lightshowCtaHeadline lightshowCtaDesc lightshowCtaCta1Text lightshowCtaCta1Link
        lightshowCtaTrust1 lightshowCtaTrust2 lightshowCtaTrust3
      }
    }
  }
`;

const VIXI_ADDONS_QUERY = `
  query GetVixiAddonsFields {
    page(id: "vixi/add-ons", idType: URI) {
      vixiAddonsFields {
        addonsHeroBadge addonsHeroTitle addonsHeroSubtitle addonsHeroDescription
        addonsHeroCta1Text addonsHeroCta1Link addonsHeroCta2Text addonsHeroCta2Link addonsHeroBgVideo
        addonsSectionHeadline addonsSectionSubtitle
        addonsAo1Title addonsAo1Desc addonsAo1Gradient addonsAo1Vimeo addonsAo1Icon addonsAo1Features
        addonsAo2Title addonsAo2Desc addonsAo2Gradient addonsAo2Vimeo addonsAo2Icon addonsAo2Features
        addonsAo3Title addonsAo3Desc addonsAo3Gradient addonsAo3Vimeo addonsAo3Icon addonsAo3Features
        addonsCtaHeadline addonsCtaDesc addonsCtaCta1Text addonsCtaCta1Link
        addonsCtaTrust1 addonsCtaTrust2 addonsCtaTrust3
      }
    }
  }
`;

const VIXI_LICENSING_QUERY = `
  query GetVixiLicensingFields {
    page(id: "vixi/licensing", idType: URI) {
      vixiLicensingFields {
        licensingHeroBadge licensingHeroTitle licensingHeroTitle2 licensingHeroDescription
        licensingHeroCta1Text licensingHeroCta1Link licensingHeroCta2Text licensingHeroCta2Link licensingHeroBgVideo
        licensingOptsHeadline licensingOptsSubtitle
        licensingOpt1Number licensingOpt1Title licensingOpt1Subtitle licensingOpt1Desc licensingOpt1Gradient licensingOpt1Icon licensingOpt1Popular licensingOpt1Highlights
        licensingOpt1CtaText licensingOpt1CtaLink
        licensingOpt2Number licensingOpt2Title licensingOpt2Subtitle licensingOpt2Desc licensingOpt2Gradient licensingOpt2Icon licensingOpt2Popular licensingOpt2Highlights
        licensingOpt2CtaText licensingOpt2CtaLink
        licensingOpt3Number licensingOpt3Title licensingOpt3Subtitle licensingOpt3Desc licensingOpt3Gradient licensingOpt3Icon licensingOpt3Popular licensingOpt3Highlights
        licensingOpt3CtaText licensingOpt3CtaLink
        licensingCtaHeadline licensingCtaDesc licensingCtaCta1Text licensingCtaCta1Link licensingCtaCta2Text licensingCtaCta2Link
        licensingCtaContact1 licensingCtaContact2 licensingCtaContact3
      }
    }
  }
`;

export async function fetchVixiCaptureFields(): Promise<VixiCaptureFields | null> {
  try {
    const data = await graphqlRequest<GQLVixiCaptureFieldsResponse>(VIXI_CAPTURE_QUERY);
    return (data.page?.vixiCaptureFields as unknown as VixiCaptureFields) || null;
  } catch (e) { console.warn('[WordPress] Failed to fetch Vixi Capture fields:', e); return null; }
}

export async function fetchVixiMagicFields(): Promise<VixiMagicFields | null> {
  try {
    const data = await graphqlRequest<GQLVixiMagicFieldsResponse>(VIXI_MAGIC_QUERY);
    return (data.page?.vixiMagicFields as unknown as VixiMagicFields) || null;
  } catch (e) { console.warn('[WordPress] Failed to fetch Vixi Magic fields:', e); return null; }
}

export async function fetchVixiStreamingFields(): Promise<VixiStreamingFields | null> {
  try {
    const data = await graphqlRequest<GQLVixiStreamingFieldsResponse>(VIXI_STREAMING_QUERY);
    return (data.page?.vixiStreamingFields as unknown as VixiStreamingFields) || null;
  } catch (e) { console.warn('[WordPress] Failed to fetch Vixi Streaming fields:', e); return null; }
}

export async function fetchVixiLightshowFields(): Promise<VixiLightshowFields | null> {
  try {
    const data = await graphqlRequest<GQLVixiLightshowFieldsResponse>(VIXI_LIGHTSHOW_QUERY);
    return (data.page?.vixiLightshowFields as unknown as VixiLightshowFields) || null;
  } catch (e) { console.warn('[WordPress] Failed to fetch Vixi LightShow fields:', e); return null; }
}

export async function fetchVixiAddonsFields(): Promise<VixiAddonsFields | null> {
  try {
    const data = await graphqlRequest<GQLVixiAddonsFieldsResponse>(VIXI_ADDONS_QUERY);
    return (data.page?.vixiAddonsFields as unknown as VixiAddonsFields) || null;
  } catch (e) { console.warn('[WordPress] Failed to fetch Vixi Add-Ons fields:', e); return null; }
}

export async function fetchVixiLicensingFields(): Promise<VixiLicensingFields | null> {
  try {
    const data = await graphqlRequest<GQLVixiLicensingFieldsResponse>(VIXI_LICENSING_QUERY);
    return (data.page?.vixiLicensingFields as unknown as VixiLicensingFields) || null;
  } catch (e) { console.warn('[WordPress] Failed to fetch Vixi Licensing fields:', e); return null; }
}


export function extractYouTubeId(url: string): string | null {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  
  return null;
}

// Extract Vimeo video ID from various URL formats
export function extractVimeoId(url: string): string | null {
  if (!url) return null;
  
  // If it's just a number, return it directly
  if (/^\d+$/.test(url.trim())) {
    return url.trim();
  }
  
  const patterns = [
    /vimeo\.com\/reviews\/[^/]+\/videos\/(\d{5,})/,
    /vimeo\.com\/(?:video\/|channels\/\w+\/|groups\/\w+\/videos\/|manage\/videos\/)?(\d{5,})/,
    /player\.vimeo\.com\/video\/(\d{5,})/,
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  
  return null;
}

// Generate video thumbnail URL from Vimeo or YouTube URL
export function getVideoThumbnail(videoUrl: string): string | null {
  if (!videoUrl) return null;
  
  // Try Vimeo first
  const vimeoId = extractVimeoId(videoUrl);
  if (vimeoId) {
    return `https://vumbnail.com/${vimeoId}.jpg`;
  }
  
  // Try YouTube
  const youtubeId = extractYouTubeId(videoUrl);
  if (youtubeId) {
    return `https://img.youtube.com/vi/${youtubeId}/maxresdefault.jpg`;
  }
  
  return null;
}
